import {
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  type MetaFunction,
  redirect,
  data,
  Form,
  Link,
  useLoaderData,
} from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import {
  createFailingUnitTestResult,
  createPassingUnitTestResult,
} from '#app/controls/unit-test.ts'
import { type Slug, executeQuery } from '#app/utils/cms.server.ts'
import { requireControlsToBeEnabled } from '#app/utils/controls.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { siteTitle, unitTestPassPercentage } from '#app/utils/global.ts'
import { type LocalizedContent } from '#app/utils/i18n.ts'
import { invariantResponse } from '#app/utils/misc.tsx'
import { Status } from '#app/utils/status.ts'
import { getActiveEnrollments } from '#app/utils/user.server.ts'

interface TrainingProgram {
  _id: string
  slug: Slug
  title: LocalizedContent
  units: Unit[]
}

interface Unit {
  _id: string
  slug: Slug
  number: number
  title: LocalizedContent
}

interface UnitTestResult {
  id: string
  unitId: string
  createdAt: Date
  completedAt: string | Date | null
  score: number | null
}

export async function loader({ params }: LoaderFunctionArgs) {
  requireControlsToBeEnabled()

  const userId = params.id
  if (!userId) {
    return redirect('/controls/unit-tests')
  }

  const user = await prisma.user.findUnique({
    where: { id: params.id },
    select: {
      id: true,
      email: true,
      unitTestResults: {
        select: {
          id: true,
          unitId: true,
          createdAt: true,
          completedAt: true,
          score: true,
        },
        orderBy: {
          createdAt: 'asc',
        },
      },
    },
  })

  if (!user) {
    return redirect('/controls/unit-tests')
  }

  const enrollments = await getActiveEnrollments(userId)
  const trainingProgramIds = enrollments.map(
    enrollment => enrollment.trainingProgramId,
  )

  const trainingProgramQuery = `*[_type == 'trainingProgram' && _id in ${JSON.stringify(trainingProgramIds)}]{_id, slug, title, units[]->{_id, slug, number, title}}`
  const trainingProgramResult = await executeQuery(trainingProgramQuery)

  if (trainingProgramResult.status !== Status.Success) {
    throw data({}, { status: 500 })
  }

  const trainingPrograms = trainingProgramResult.data as TrainingProgram[]

  return {
    trainingPrograms,
    user,
  }
}

export async function action({ params, request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const intent = formData.get('intent')

  const userId = params.id
  invariantResponse(typeof userId === 'string', 'User ID must be a string')

  switch (intent) {
    case 'deleteTestResult': {
      const testResultId = formData.get('testResultId')
      invariantResponse(
        typeof testResultId === 'string',
        'Unit Test Result ID must be a string',
      )

      await prisma.unitTestResult.delete({
        where: { id: testResultId },
      })

      return redirect(`/controls/unit-tests/${userId}`)
    }
    case 'addPassingTestResult': {
      const unitId = formData.get('unitId')
      invariantResponse(typeof unitId === 'string', 'Unit ID must be a string')
      invariantResponse(typeof userId === 'string', 'User ID must be a string')

      await createPassingUnitTestResult(userId, unitId)
      return redirect(`/controls/unit-tests/${userId}`)
    }
    case 'addFailingTestResult': {
      const unitId = formData.get('unitId')
      invariantResponse(typeof unitId === 'string', 'Unit ID must be a string')
      invariantResponse(typeof userId === 'string', 'User ID must be a string')

      await createFailingUnitTestResult(userId, unitId)
      return redirect(`/controls/unit-tests/${userId}`)
    }
    default: {
      throw new Response(`${intent} intent is not supported`, {
        status: 400,
      })
    }
  }
}

const UnitTestsControl = ({
  trainingPrograms,
  unitTestResults,
}: {
  trainingPrograms: TrainingProgram[]
  unitTestResults: UnitTestResult[]
}) => (
  <>
    <h2 className="text-h3">Unit Tests</h2>
    {trainingPrograms.map(trainingProgram => (
      <div className="mt-4" key={trainingProgram._id}>
        <h3 className="font-bold">{trainingProgram.title.en}</h3>
        <ul className="ml-4">
          {trainingProgram.units.map(unit => {
            const currentUnitTestResults = unitTestResults.filter(
              testResult => testResult.unitId === unit._id,
            )
            return (
              <li key={unit._id} className="mt-4">
                Unit {unit.number}: {unit.title.en}
                <div className="ml-4">
                  <Link
                    to={`/training/${trainingProgram.slug.current}/${unit.slug.current}/unit-test`}
                  >
                    View unit test
                  </Link>
                  {currentUnitTestResults.length > 0 && (
                    <div>
                      <Link
                        to={`/training/${trainingProgram.slug.current}/${unit.slug.current}/unit-test/results`}
                      >
                        View results for most recently completed test
                      </Link>
                    </div>
                  )}
                  <Form method="POST">
                    <input type="hidden" name="unitId" value={unit._id} />
                    <Button
                      type="submit"
                      name="intent"
                      value="addPassingTestResult"
                      variant="link"
                    >
                      Create a Passing Unit Test
                    </Button>
                  </Form>
                  <Form method="POST">
                    <input type="hidden" name="unitId" value={unit._id} />
                    <Button
                      type="submit"
                      name="intent"
                      value="addFailingTestResult"
                      variant="link"
                    >
                      Create a Failing Unit Test
                    </Button>
                  </Form>
                  {currentUnitTestResults.length === 0 && (
                    <div>No test results</div>
                  )}
                  {currentUnitTestResults.length > 0 && (
                    <>
                      <div className="mt-4">Test Results:</div>
                      <table className="border-collapse border border-slate-500">
                        <thead>
                          <tr>
                            <th className="border border-slate-500 px-2">
                              Deletion
                            </th>
                            <th className="border border-slate-500 px-2">
                              Started At
                            </th>
                            <th className="border border-slate-500 px-2">
                              Completed At
                            </th>
                            <th className="border border-slate-500 px-2">
                              Passed
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentUnitTestResults.map(testResult => {
                            const testStartedAt = new Date(
                              testResult.createdAt,
                            ).toLocaleString()
                            const testCompletedAt = testResult.completedAt
                              ? new Date(
                                  testResult.completedAt,
                                ).toLocaleString()
                              : 'In progress'
                            const testPassed = testResult.score
                              ? testResult.score >= unitTestPassPercentage
                              : false
                            return (
                              <tr key={testResult.id}>
                                <td className="border border-slate-500 px-2">
                                  <Form method="POST" className="inline">
                                    <input
                                      type="hidden"
                                      name="testResultId"
                                      value={testResult.id}
                                    />
                                    <Button
                                      type="submit"
                                      name="intent"
                                      value="deleteTestResult"
                                      variant="link"
                                      size="sm"
                                      className="mr-2"
                                    >
                                      Delete
                                    </Button>
                                  </Form>
                                </td>
                                <td className="border border-slate-500 px-2">
                                  {testStartedAt}
                                </td>
                                <td className="border border-slate-500 px-2">
                                  {testCompletedAt}
                                </td>
                                <td className="border border-slate-500 px-2">
                                  {testResult.score !== null && (
                                    <>
                                      <span>{testPassed ? `✔️` : `❌`}</span>
                                      <span className="pl-2">{`${testResult.score}%`}</span>
                                    </>
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    ))}
  </>
)

export default function UserUnitTestsControlRoute() {
  const data = useLoaderData<typeof loader>()
  const { trainingPrograms, user } = data

  return (
    <>
      <ul className="flex gap-3">
        <li>
          <Link to={'/controls'}>Controls</Link>
        </li>
        <li className="flex items-center gap-3">
          ▶️
          <span className="inline-flex items-center gap-1.5">
            <Link to={'/controls/unit-tests'}>Unit Tests</Link>
          </span>
        </li>
        <li className="flex items-center gap-3">
          ▶️
          <span className="inline-flex items-center gap-1.5">{user.email}</span>
        </li>
      </ul>
      <dl>
        <dt className="mt-6 font-bold">User ID</dt>
        <dd>{user.id}</dd>
        <dt className="mt-6 font-bold">Email</dt>
        <dd>{user.email}</dd>
      </dl>
      <hr className="mt-6" />
      <div className="mt-6">
        <UnitTestsControl
          trainingPrograms={trainingPrograms}
          unitTestResults={user.unitTestResults}
        />
      </div>
    </>
  )
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    {
      title: `Controls > Unit Tests > ${
        data ? data.user.email : 'User'
      } | ${siteTitle}`,
    },
  ]
}
